import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@acme/ui/card";
import { Label } from "@acme/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@acme/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import { cn } from "@acme/ui";

export const Route = createFileRoute("/_authed/my-players/$tag/matchs")({
  component: MatchsTab,
});

const FILTER_OPTIONS = [
  { value: "all", label: "All matches" },
  { value: "war", label: "War filter" },
  { value: "cwl", label: "CWL filter" },
  { value: "friendly", label: "Friendly filter" },
] as const;

// Fake data for placeholder: clans with numeric stats. Highlight max per column.
const FAKE_MATCHES = [
  { id: "1", clanName: "Alpha Warriors", clanTag: "#ABC123", trophies: 2450, level: 14, stars: 28, destruction: 92 },
  { id: "2", clanName: "Beta Legends", clanTag: "#DEF456", trophies: 2680, level: 15, stars: 30, destruction: 98 },
  { id: "3", clanName: "Gamma Force", clanTag: "#GHI789", trophies: 2100, level: 13, stars: 24, destruction: 85 },
  { id: "4", clanName: "Delta Elite", clanTag: "#JKL012", trophies: 2720, level: 15, stars: 30, destruction: 96 },
  { id: "5", clanName: "Epsilon Kings", clanTag: "#MNO345", trophies: 2550, level: 14, stars: 27, destruction: 91 },
];

type MatchRow = (typeof FAKE_MATCHES)[number];

const NUMERIC_KEYS = ["trophies", "level", "stars", "destruction"] as const;

function MatchsTab()
{
  const [filterValue, setFilterValue] = useState<string>("all");

  const { maxByKey, isMax } = useMemo(() =>
  {
    const maxByKey: Record<string, number> = {};
    for (const key of NUMERIC_KEYS)
    {
      const values = FAKE_MATCHES.map((row) => row[key]);
      maxByKey[key] = Math.max(...values);
    }
    const isMax = (row: MatchRow, key: (typeof NUMERIC_KEYS)[number]) => row[key] === maxByKey[key];
    return { maxByKey, isMax };
  }, []);

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="filter">Which search filter matches to show</Label>
          <Select value={filterValue} onValueChange={setFilterValue}>
            <SelectTrigger id="filter" className="w-full max-w-xs">
              <SelectValue placeholder="Select filter..." />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border border-input overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clan</TableHead>
                <TableHead className="text-right">Trophies</TableHead>
                <TableHead className="text-right">Level</TableHead>
                <TableHead className="text-right">Stars</TableHead>
                <TableHead className="text-right">Destruction %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {FAKE_MATCHES.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{row.clanName}</span>
                      <span className="text-xs text-muted-foreground font-mono">{row.clanTag}</span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      isMax(row, "trophies") && "font-semibold text-foreground bg-primary/10"
                    )}>
                    {row.trophies.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      isMax(row, "level") && "font-semibold text-foreground bg-primary/10"
                    )}>
                    {row.level}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      isMax(row, "stars") && "font-semibold text-foreground bg-primary/10"
                    )}>
                    {row.stars}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      isMax(row, "destruction") && "font-semibold text-foreground bg-primary/10"
                    )}>
                    {row.destruction}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground">
          Placeholder data. Max value per column is highlighted. Replace with real match data when backend is ready.
        </p>
      </div>
    </Card>
  );
}
